import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const FIELDS = [
  { key: "business_name", label: "Business Name" },
  { key: "tagline", label: "Tagline" },
  { key: "phone", label: "Phone (display)" },
  { key: "phone_raw", label: "Phone (tel link, e.g. +9199...)" },
  { key: "whatsapp", label: "WhatsApp Number (e.g. 9199...)" },
  { key: "email", label: "Email" },
  { key: "address", label: "Address", full: true },
  { key: "logo", label: "Logo URL", full: true },
  { key: "maps_link", label: "Google Maps Link (Get Directions)", full: true },
  { key: "maps_embed", label: "Google Maps Embed URL (iframe src)", full: true },
  { key: "facebook", label: "Facebook URL" },
  { key: "instagram", label: "Instagram URL" },
  { key: "youtube", label: "YouTube URL" },
  { key: "twitter", label: "Twitter / X URL" },
];

export default function SettingsAdmin() {
  const qc = useQueryClient();
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setForm(r.data || {})); }, []);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setLoading(true);
    try { await api.put("/admin/settings", form); qc.invalidateQueries(); toast.success("Settings saved"); }
    catch { toast.error("Save failed"); } finally { setLoading(false); }
  };

  const inp = "w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-brand";
  return (
    <div data-testid="admin-settings" className="max-w-3xl">
      <h1 className="text-2xl font-heading font-bold text-navy-heading mb-1">Website Settings</h1>
      <p className="text-slate-500 text-sm mb-6">Business info, contact details and social links used across the website.</p>
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        {FIELDS.map((f) => (
          <div key={f.key} className={f.full ? "sm:col-span-2" : ""}>
            <label className="block text-xs font-semibold text-navy-heading mb-1.5">{f.label}</label>
            <input className={inp} value={form[f.key] || ""} onChange={set(f.key)} data-testid={`setting-${f.key}`} />
          </div>
        ))}
        {form.logo && <div className="sm:col-span-2"><img src={form.logo} alt="logo" className="h-16 w-16 rounded-full object-cover" /></div>}
      </div>
      <button onClick={save} disabled={loading} className="mt-5 inline-flex items-center gap-2 bg-brand text-white font-semibold px-6 py-3 rounded-xl disabled:opacity-70" data-testid="settings-save-btn">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Settings</button>
    </div>
  );
}
